import { map } from 'rxjs/operators';

import { mapPaginated } from '@sb/feathers-types';
import type { Routine, Unsubscribe } from '@sb/remote-control/types';
import { convertRoutineResponse } from '@sb/remote-control/util/convertRoutineResponse';

import { getLocateSkillByIdFromStore } from '../skills/skillsStore';

import { getRoutinesService } from './utils';

export interface OnGetRoutineOptions {
  /**
   * Include server-hidden routines (e.g. ephemeral one-off runs that the
   * server stamps `isHidden: true`) in the subscription. Defaults to `false`
   * so list / editor / prefetch callers continue to see only durable
   * routines. Opt-in is intended for the loaded-routine pipeline
   * (`useRoutineData`), which feeds dashboard surfaces and widgets that
   * must reflect whatever the runner currently has loaded — including
   * ephemeral runs.
   */
  includeHidden?: boolean;
}

export function onGetRoutine(
  routineID: string,
  onSnapshot: (routine: Routine.ConvertedResponse | null) => void,
  options: OnGetRoutineOptions = {},
): Unsubscribe {
  // The server-side `promoteQueryFlags` hook lifts `query.includeHidden`
  // onto `params.includeHidden` before `filterHidden` runs, which is how
  // the opt-in actually bypasses the default `isHidden: true` filter.
  const query: Record<string, unknown> = { id: routineID, $limit: 1 };

  if (options.includeHidden) {
    query.includeHidden = true;
  }

  const subscription = getRoutinesService()
    .watch()
    .find({ query })
    .pipe(
      map((response) => {
        const routine = mapPaginated(response, (data) =>
          convertRoutineResponse(data, {
            getLocateSkillById: getLocateSkillByIdFromStore,
          }),
        )[0];

        return routine ?? null;
      }),
    )
    .subscribe(onSnapshot);

  return () => subscription.unsubscribe();
}
